import { apiRequest } from './apiRequest';

export const projectsAPI = {
  listByStream: async (orgId, streamId, options: any = {}) => {
    const qs = options.stats ? '?stats=true' : '';
    const data = await apiRequest<any>(`/api/organisations/${orgId}/streams/${streamId}/projects${qs}`, {
      method: 'GET',
    });
    return data.projects ?? [];
  },
  listDropdownByStream: async (orgId, streamId) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/dropdown`,
      { method: 'GET' }
    );
    return data.projects ?? [];
  },
  get: async (orgId, streamId, projectId) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${projectId}`,
      { method: 'GET' }
    );
    return data.project ?? data;
  },
  getCardStats: async (orgId, streamId, projectId, options: any = {}) => {
    const params = new URLSearchParams();
    const tf = String(options.timeframe || 'month').toLowerCase();
    if (tf === 'week' || tf === 'month' || tf === 'year') params.set('timeframe', tf);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${encodeURIComponent(projectId)}/card-stats${qs}`,
      { method: 'GET' }
    );
    return data.project ?? null;
  },
  create: async (orgId, streamId, body) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/streams/${streamId}/projects`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return data.project ?? data;
  },
  update: async (orgId, streamId, projectId, body) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${projectId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      }
    );
    return data.project ?? data;
  },
  delete: async (orgId, streamId, projectId) => {
    await apiRequest<any>(`/api/organisations/${orgId}/streams/${streamId}/projects/${projectId}`, {
      method: 'DELETE',
    });
  },
  getDashboard: async (orgId, streamId, projectId) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${projectId}/dashboard`,
      { method: 'GET' }
    );
    return data.stats ?? data;
  },
  getDora: async (orgId, streamId, projectId, windowDays = 30) => {
    const params = new URLSearchParams({ windowDays: String(windowDays) });
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${projectId}/dora?${params}`,
      { method: 'GET' }
    );
    return { dora: data.dora, meta: data.meta };
  },
  getDoraLabelMetrics: async (orgId, streamId, projectId, options: any = {}) => {
    const params = new URLSearchParams();
    if (options.windowDays != null) params.set('windowDays', String(options.windowDays));
    if (options.granularity === 'week') params.set('granularity', 'week');
    const qs = params.toString();
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${projectId}/dora-label-metrics${qs ? `?${qs}` : ''}`,
      { method: 'GET' }
    );
    return { labelDora: data.labelDora, meta: data.meta };
  },
  getCycleTime: async (orgId, streamId, projectId, options: any = {}) => {
    const params = new URLSearchParams();
    if (options.windowDays != null) params.set('windowDays', String(options.windowDays));
    const qs = params.toString();
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${projectId}/cycle-time${qs ? `?${qs}` : ''}`,
      { method: 'GET' }
    );
    return { cycleTime: data.cycleTime, meta: data.meta };
  },
  getChurn: async (orgId, streamId, projectId, windowDays = 30) => {
    const params = new URLSearchParams({ windowDays: String(windowDays) });
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${projectId}/churn?${params}`,
      { method: 'GET' }
    );
    const churn = data.churn ?? data;
    return { dates: churn.dates ?? [], ratios: churn.ratios ?? [] };
  },
  getMembers: async (orgId, streamId, projectId) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${projectId}/members`,
      { method: 'GET' }
    );
    return data.members ?? [];
  },
  addMember: async (orgId, streamId, projectId, userId) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${projectId}/members`,
      { method: 'POST', body: JSON.stringify({ userId }) }
    );
    return data.members ?? [];
  },
  removeMember: async (orgId, streamId, projectId, userId) => {
    await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${projectId}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );
  },
  /** Queue code index jobs for all repositories connected to the project (requires api.projects.codeIndex.trigger). */
  triggerCodeIndex: async (orgId, streamId, projectId) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${encodeURIComponent(projectId)}/code-index/trigger`,
      { method: 'POST', body: JSON.stringify({}) }
    );
    return { queued: data.queued ?? 0, repositoryIds: data.repositoryIds ?? [] };
  },

  getPortfolioWiki: async (orgId, streamId, projectId, options: any = {}) => {
    const qs = options.includeRetrievalDebug === true ? '?includeRetrievalDebug=1' : '';
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${encodeURIComponent(projectId)}/portfolio-wiki${qs}`,
      { method: 'GET' }
    );
    return {
      wiki: data.wiki ?? null,
      readiness: data.readiness ?? {},
    };
  },
  patchPortfolioWiki: async (orgId, streamId, projectId, body) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${encodeURIComponent(projectId)}/portfolio-wiki`,
      { method: 'PATCH', body: JSON.stringify(body) }
    );
    return data.wiki ?? null;
  },
  regeneratePortfolioWiki: async (orgId, streamId, projectId) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${encodeURIComponent(projectId)}/portfolio-wiki/regenerate`,
      { method: 'POST', body: JSON.stringify({}) }
    );
    return data.wiki ?? null;
  },
  listPortfolioAuthorAssignments: async (orgId, streamId, projectId) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${encodeURIComponent(projectId)}/portfolio-wiki/author-assignments`,
      { method: 'GET' }
    );
    return data.assignments ?? [];
  },
  putPortfolioAuthorAssignments: async (orgId, streamId, projectId, assignments) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${encodeURIComponent(projectId)}/portfolio-wiki/author-assignments`,
      { method: 'PUT', body: JSON.stringify({ assignments }) }
    );
    return data.assignments ?? [];
  },
  listKnowledgeSources: async (orgId, streamId, projectId, options: any = {}) => {
    const qs = new URLSearchParams();
    if (options.repositoryId != null && String(options.repositoryId).trim() !== '') {
      qs.set('repositoryId', String(options.repositoryId).trim());
    }
    const q = qs.toString();
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${encodeURIComponent(projectId)}/knowledge-sources${q ? `?${q}` : ''}`,
      { method: 'GET' }
    );
    return data.sources ?? [];
  },
  createKnowledgeUrl: async (
    orgId,
    streamId,
    projectId,
    { url, title, repositoryId }: { url: string; title?: string; repositoryId?: string },
  ) => {
    const body: { url: string; title?: string; repositoryId?: string } = { url };
    if (title != null && String(title).trim() !== '') body.title = String(title).trim();
    if (repositoryId != null && String(repositoryId).trim() !== '')
      body.repositoryId = String(repositoryId).trim();
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${encodeURIComponent(projectId)}/knowledge-sources`,
      { method: 'POST', body: JSON.stringify(body) }
    );
    return data.source ?? null;
  },
  uploadKnowledgeFile: async (orgId, streamId, projectId, file, options: any = {}) => {
    const form = new FormData();
    form.append('file', file);
    if (options.repositoryId != null && String(options.repositoryId).trim() !== '') {
      form.append('repositoryId', String(options.repositoryId).trim());
    }
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${encodeURIComponent(projectId)}/knowledge-sources/upload`,
      { method: 'POST', body: form }
    );
    return data.source ?? null;
  },
  patchKnowledgeSource: async (orgId, streamId, projectId, sourceId, body) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${encodeURIComponent(projectId)}/knowledge-sources/${encodeURIComponent(sourceId)}`,
      { method: 'PATCH', body: JSON.stringify(body) }
    );
    return data.source ?? null;
  },
  deleteKnowledgeSource: async (orgId, streamId, projectId, sourceId) => {
    await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${encodeURIComponent(projectId)}/knowledge-sources/${encodeURIComponent(sourceId)}`,
      { method: 'DELETE' }
    );
  },
  reprocessKnowledgeSource: async (orgId, streamId, projectId, sourceId) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/projects/${encodeURIComponent(projectId)}/knowledge-sources/${encodeURIComponent(sourceId)}/reprocess`,
      { method: 'POST', body: JSON.stringify({}) }
    );
    return data.source ?? null;
  },
};
